import AppState from "store/AppState";
import { MetricCategory, PieChartDataState } from "./PerformanceReportTypes";
import { selectStoreInfoById } from "store/store-list/StoreListSelectors";
import { selectCatalogListNew } from "store/catalog/CatalogSelectors";

const defaultPieChartState: PieChartDataState = {
  data: null,
  isLoading: false,
};

export const selectPerformanceReportStoreId = (state: AppState) =>
  state.performanceReport.reportStoreId;

export const selectPerformanceReportDateRange = (state: AppState) =>
  state.performanceReport.reportDateRange;

export const selectPerformanceReportAnalyticsConfig = (state: AppState) =>
  state.performanceReport.reportAnalyticsConfig;

export const selectMetrics = (category: MetricCategory) => (state: AppState) =>
  state?.performanceReport?.metrics[category];

export const selectTrafficMetrics = (state: AppState) =>
  selectMetrics(MetricCategory.TRAFFIC)(state);

export const selectIsMetricsLoading =
  (category: MetricCategory) => (state: AppState) =>
    state.performanceReport.metrics[category].isLoading;

export const selectPieChartDataByMetricId =
  (metricId: string) =>
  (state: AppState): PieChartDataState => {
    return state.performanceReport.pieCharts[metricId] || defaultPieChartState;
  };

export const selectTrafficBySourceData = (state: AppState) =>
  selectPieChartDataByMetricId("trafficBySource")(state);

export const selectTrafficByDeviceData = (state: AppState) =>
  selectPieChartDataByMetricId("trafficByDevice")(state);

export const selectOrdersBySourceData = (state: AppState) =>
  selectPieChartDataByMetricId("ordersBySource")(state);

export const selectIsReportAPIAvailable = (state: AppState) =>
  state.performanceReport.reportAnalyticsConfig?.operations?.includes(
    "POST /v1/{accountId}/report",
  ) as boolean;

export const selectConversionMetrics = (state: AppState) =>
  selectMetrics(MetricCategory.CONVERSION)(state);

export const selectProductMetrics = (state: AppState) =>
  selectMetrics(MetricCategory.PRODUCT)(state);

export const selectRevenueMetrics = (state: AppState) =>
  selectMetrics(MetricCategory.REVENUE)(state);

export const selectKPIViewProducts = (state: AppState) =>
  state.performanceReport.kpiViewProducts?.data;

export const selectIsKPIProductsLoading = (state: AppState) =>
  state.performanceReport.isKPIProductsLoading;

export const selectKPIProductDetails = (state: AppState, productId: string) => {
  return (
    state.performanceReport.kpiProductDetails.find(
      (product) => product.productId === productId,
    ) ?? null
  );
};

export const selectOrdersByDeviceData = (state: AppState) =>
  selectPieChartDataByMetricId("ordersByDevice")(state);

export const selectLocationTreeData = (state: AppState) =>
  state.performanceReport.locationTree.data;

export const selectLocationTreeIsLoading = (state: AppState) => {
  const globalLoading = state.performanceReport.locationTree.isLoading;
  const checkNodeLoading = (nodes: any[]): boolean => {
    return nodes.some((node) => {
      if (node.isLoading) return true;
      if (node.children && Array.isArray(node.children)) {
        return checkNodeLoading(node.children);
      }
      return false;
    });
  };
  return (
    globalLoading || checkNodeLoading(state.performanceReport.locationTree.data)
  );
};

export const selectLocationTreeExpandedNodes = (state: AppState) =>
  state.performanceReport.locationTree.expandedNodes;

export const selectLocationTreeHasData = (state: AppState) => {
  const data = state.performanceReport.locationTree.data;
  const hasRootData = Array.isArray(data) && data.length > 0;
  const hasExpandedData = (nodes: any[]): boolean => {
    if (!Array.isArray(nodes)) return false;
    return nodes.some((node) => {
      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        return true;
      }
      if (node.children && Array.isArray(node.children)) {
        return hasExpandedData(node.children);
      }
      return false;
    });
  };
  return hasRootData || hasExpandedData(data || []);
};

export const selectLocationTreeTotalValue = (state: AppState) => {
  const data = state.performanceReport.locationTree.data;
  return data.reduce((total, node) => total + (node.metricValue || 0), 0);
};

export const selectKPIAnalysis = (state: AppState) =>
  state.performanceReport.kpiAnalysis.data;

export const selectKPIAnalysisLoading = (state: AppState) =>
  state.performanceReport.kpiAnalysis.isLoading;

export const selectDateYearMetrics = (state: AppState) =>
  state.performanceReport.dateYearMetrics.data;

export const selectDateYearMetricsLoading = (state: AppState) =>
  state.performanceReport.dateYearMetrics.isLoading;

export const selectKPIAnalysisRetryAfter = (state: AppState) =>
  state.performanceReport.kpiAnalysis.retryAfter;

export const selectKPIAnalysisNotFound = (state: AppState) =>
  state.performanceReport.kpiAnalysis.is404Error;

export const selectShouldDisableSummaryTab = (state: AppState) =>
  state.performanceReport.kpiAnalysis.shouldDisableSummaryTab;

export const selectKPIStoreList = (state: AppState) =>
  state.performanceReport.kpiStoreList.stores;

export const selectKPIStoreListLoading = (state: AppState) =>
  state.performanceReport.kpiStoreList.isLoading;

export const selectKPIStoreListLoaded = (state: AppState) =>
  state.performanceReport.kpiStoreList.isLoaded;

export const selectIsMetricsAlreadyFetched =
  (category: MetricCategory) => (state: AppState) => {
    const lastFetched = state.performanceReport.lastFetchedParams[category];
    if (!lastFetched) return false;

    return (
      lastFetched.storeId === state.performanceReport.reportStoreId &&
      lastFetched.dateRange === state.performanceReport.reportDateRange
    );
  };

export const selectIsTrafficMetricsAlreadyFetched = (state: AppState) =>
  selectIsMetricsAlreadyFetched(MetricCategory.TRAFFIC)(state);

export const selectIsRevenueMetricsAlreadyFetched = (state: AppState) =>
  selectIsMetricsAlreadyFetched(MetricCategory.REVENUE)(state);

export const selectIsConversionMetricsAlreadyFetched = (state: AppState) =>
  selectIsMetricsAlreadyFetched(MetricCategory.CONVERSION)(state);

export const selectIsProductMetricsAlreadyFetched = (state: AppState) =>
  selectIsMetricsAlreadyFetched(MetricCategory.PRODUCT)(state);

export const selectPerformanceReportLocale = (
  state: AppState,
): string | null => {
  const reportStoreId = state.performanceReport.reportStoreId;
  if (!reportStoreId) {
    return null;
  }

  const storeInfo = selectStoreInfoById(state, reportStoreId);
  if (!storeInfo) {
    return null;
  }

  // Return default localeCode if available
  if (storeInfo.storeDefaults?.localeCode) {
    return storeInfo.storeDefaults.localeCode;
  }

  // Otherwise return the first supported localeCode
  if (storeInfo.localeCodes && storeInfo.localeCodes.length > 0) {
    return storeInfo.localeCodes[0].localeCode;
  }

  return null;
};

export const selectPerformanceReportCatalogId = (
  state: AppState,
): string | null => {
  const reportStoreId = state.performanceReport.reportStoreId;
  if (!reportStoreId) {
    return null;
  }

  const storeInfo = selectStoreInfoById(state, reportStoreId);
  if (!storeInfo) {
    return null;
  }

  // Return default catalogId if available
  if (storeInfo.storeDefaults?.catalogId) {
    return storeInfo.storeDefaults.catalogId;
  }

  // Otherwise return the first catalog in the catalog list for this store
  const catalogList = selectCatalogListNew(state);
  const storeCatalogs = catalogList[reportStoreId];
  if (storeCatalogs && storeCatalogs.length > 0) {
    return storeCatalogs[0];
  }

  return null;
};
